/**
 * A tela de leitura de uma entidade: quem ela é, com o que se liga, e o grafo.
 *
 * `PainelDossie` e `PainelGrafo` já existiam e já eram heterogêneos (`{ownerTipo, ownerId}`), mas
 * estavam presos DENTRO da tela de edição — só se chegava neles abrindo um cadastro para alterar.
 * Aqui eles viram destino da busca: o operador digita um CPF e cai direto na correlação, sem passar
 * por um formulário que ele não veio preencher.
 *
 * **A ficha veio depois, e por um defeito.** Nas primeiras versões esta tela só tinha os dois
 * painéis de relação, então uma pessoa sem correlação abria completamente vazia: duas caixas
 * dizendo "ainda não" e nem o nome dela no título, que trazia a palavra "Pessoa". O operador
 * acabava de encontrá-la numa busca de 1.972 resultados e a tela não lhe devolvia nada. Correlação
 * é o que o sistema DEDUZ; o cadastro é o que ele SABE — e o que ele sabe vem primeiro.
 */
import { Pencil } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FichaEntidade } from '@/components/crud/FichaEntidade';
import { PainelDossie } from '@/components/crud/paineis/PainelDossie';
import { PainelGrafo } from '@/components/crud/paineis/PainelGrafo';
import { Button } from '@/components/ui/button';
import { Pagina } from '@/components/ui/pagina';
import { Skeleton } from '@/components/ui/misc';
import { api, type DossieCad } from '@/lib/api';
import { RECURSOS, ROTA_POR_OWNER, rotaDe } from '@/lib/recursos';
import { rotuloTipo } from '@/lib/recursos/rotulos';
import type { OwnerTipo, RecursoBase } from '@/lib/recursos/tipos';

export default function Entidade() {
  const { ownerTipo = '', id = '' } = useParams();
  const navegar = useNavigate();
  const chave = ROTA_POR_OWNER[ownerTipo];
  const descritor = chave ? RECURSOS[chave] : undefined;

  const [registro, setRegistro] = useState<RecursoBase | null>(null);
  const [dossie, setDossie] = useState<DossieCad | null>(null);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    if (!descritor || !id) return;
    let vivo = true;
    setCarregando(true);
    setRegistro(null);
    // Os dois em paralelo: a ficha vem do cadastro, contatos e endereços vêm do dossiê (moram em
    // tabelas próprias, compartilhadas — é o que permite duas pessoas dividirem um telefone).
    void Promise.all([
      api.get<RecursoBase>(`${descritor.endpoint}/${id}`),
      api.get<DossieCad>(`/cadastro/dossie/${ownerTipo}/${id}`),
    ]).then(([reg, dos]) => {
      if (!vivo) return;
      setCarregando(false);
      if (reg.ok) setRegistro(reg.dados);
      if (dos.ok) setDossie(dos.dados);
    });
    return () => {
      vivo = false;
    };
  }, [descritor, ownerTipo, id]);

  if (!descritor || !id) {
    return <p className="tipo-corpo text-text-tertiary">Entidade desconhecida.</p>;
  }

  // O título é o NOME, não a espécie: quem chegou aqui por uma busca precisa confirmar, de relance,
  // que abriu quem queria. `rotuloItem` é o mesmo que nomeia a linha na lista.
  const nome = registro ? descritor.rotuloItem(registro) : '';

  return (
    <Pagina
      titulo={nome || rotuloTipo(ownerTipo)}
      descricao={nome ? rotuloTipo(ownerTipo) : undefined}
      aoVoltar={() => navegar(-1)}
      acoes={
        /* A leitura é o padrão; editar é uma escolha explícita. */
        <Button variant="secundario" onClick={() => navegar(rotaDe(ownerTipo, id))}>
          <Pencil className="size-4" />
          Abrir para editar
        </Button>
      }
    >
      {carregando && !registro ? (
        <Skeleton className="h-40 w-full" />
      ) : registro ? (
        <FichaEntidade
          descritor={descritor}
          registro={registro}
          extras={[
            { rotulo: 'Telefones', valores: dossie?.telefones ?? [] },
            { rotulo: 'E-mails', valores: dossie?.emails ?? [] },
            { rotulo: 'Endereços', valores: dossie?.enderecos ?? [] },
          ]}
        />
      ) : null}

      <PainelDossie ownerTipo={ownerTipo as OwnerTipo} ownerId={id} />
      <PainelGrafo ownerTipo={ownerTipo as OwnerTipo} ownerId={id} />
    </Pagina>
  );
}
